import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  generateAiWheelAction,
  addPendingWheel,
} from "../../redux/actions/wheels";
import { useGenerateAiWheel } from "../../services/mutations";
import ToastMessage from "../../sharedcomponents/Toast";
import { ASSETS, TOAST_TYPE } from "../../utils/config";
import { handleFileUpload } from "../../utils/fileUpload";
import AIGenerationSuccess from "./AIGenerationSuccess";
import AIGenerator from "./AIGenerator";

const allowedMimeTypes = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "heic",
  "doc",
  "xls",
  "xlsx",
  "docx",
];

function NewWheelPopup({ openWheelPopup, handleCloseWheelPopup }) {
  const [showAiGeneration, setShowAiGeneration] = useState(false);
  const [data, setData] = useState({
    prompt: "",
    segmentCount: 4,
    wordCount: 100,
    variation: 0.2,
  });
  const [showAiGenerationSuccess, setShowAiGenerationSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const dispatch = useDispatch();
  const autoGenerateWheel = useGenerateAiWheel();

  const isMimeTypeAllowed = (type) => {
    return allowedMimeTypes.includes(type);
  };

  const handleInput = (key, value) => {
    setData({ ...data, [key]: value });
  };

  const uploadFiles = async (files) => {
    const temp = [];
    const promises = files.map(async (file) => {
      const res = await handleFileUpload(file.file);
      temp.push({
        ...file,
        location: res.location,
        loading: false,
      });
    });

    await Promise.all(promises);
    return temp;
  };

  const handleFileChange = async (selectedFiles) => {
    const filesArray =
      selectedFiles instanceof FileList
        ? Array.from(selectedFiles)
        : [...selectedFiles];

    const updatedFiles = [];
    if (filesArray.length > 0) {
      filesArray.forEach((file) => {
        const mimeType = file.name.split(".").pop().toLowerCase();
        if (isMimeTypeAllowed(mimeType)) {
          const id = Math.floor(Date.now() + Math.random());
          updatedFiles.push({ id, mimeType, file, loading: true });
        } else {
          alert("File format not supported for file: " + file.name);
        }
      });

      setFiles((prevFiles) => [...prevFiles, ...updatedFiles]);

      const uploadedFiles = await uploadFiles(updatedFiles);
      setFiles((prevFiles) =>
        prevFiles.map((prevFile) => {
          const matchingUploadedFile = uploadedFiles.find(
            (uploadedFile) => uploadedFile.id === prevFile.id
          );
          return matchingUploadedFile
            ? { ...prevFile, ...matchingUploadedFile, loading: false }
            : prevFile;
        })
      );
    }
  };

  const handleFileRemove = (id) => {
    const temp = [...files];
    setFiles(temp.filter((file) => file.id !== id));
  };

  const closeModalHandler = () => {
    handleCloseWheelPopup();
    setShowAiGeneration(false);
    setFiles([]);
    setData({
      prompt: "",
      variation: 0.2,
      segmentCount: 4,
      wordCount: 100,
    });
    setShowAiGenerationSuccess(false);
  };

  const hasWords = (text) => {
    return /\w/.test(text);
  };

  const handleSubmit = async () => {
    if (
      data.prompt.length > 5 &&
      hasWords(data.prompt) &&
      !files.some((item) => item.loading)
    ) {
      setLoading(true);
      const { prompt, wordCount, segmentCount, variation } = data;
      const temp_id = Math.floor(Date.now() + Math.random());
      const media = [];
      const documents = [];

      if (files.length) {
        files.forEach((file) => {
          const { mimeType } = file;
          if (mimeType === "png" || mimeType === "jpg" || mimeType === "jpeg")
            media.push(file.location);
          else documents.push(file.location);
        });
      }
      const payload = {
        prompt,
        temperature: variation,
        segment_count: segmentCount,
        word_count: wordCount,
        temp_id,
        media,
        documents,
      };

      console.log(payload);

      autoGenerateWheel.mutate(payload, {
        onSuccess: (response) => {
          setShowAiGenerationSuccess(true);
          dispatch(
            addPendingWheel({
              ...payload,
              startTime: new Date(),
            })
          );
          setLoading(false);
          setFiles([]);
          setData({
            prompt: "",
            variation: 0.2,
            segmentCount: 4,
            wordCount: 100,
          });
        },
        onError: (err) => {
          console.log("error: " + err.response.data.message);
          closeModalHandler();
          setLoading(false);
          setFiles([]);
          setData({
            prompt: "",
            variation: 0.2,
            segmentCount: 4,
            wordCount: 100,
          });
        },
      });
    } else {
      return (
        <>
          <ToastMessage
            message={"Please enter correct input"}
            type={TOAST_TYPE.WARNING}
          />
        </>
      );
    }
  };
  const renderAiGeneration = () => {
    return showAiGenerationSuccess ? (
      <AIGenerationSuccess closeModalHandler={closeModalHandler} />
    ) : (
      <AIGenerator
        closeModalHandler={closeModalHandler}
        files={files}
        handleFileRemove={handleFileRemove}
        handleFileChange={handleFileChange}
        handleSubmit={handleSubmit}
        loading={loading}
        handleInput={handleInput}
        data={data}
      />
    );
  };

  return (
    <Modal
      open={openWheelPopup}
      onClose={closeModalHandler}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      {showAiGeneration ? (
        renderAiGeneration()
      ) : (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "45vw",
            minWidth: 600,
            minHeight: 350,
            height: "45vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            cursor: "pointer",
            borderRadius: "20px",
            backgroundImage: `url(${ASSETS.gradientBg})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: "8vh",
            }}
          >
            <Typography
              variant="h4"
              component="h4"
              style={{
                fontFamily: "DM Sans",
                fontWeight: "600",
                fontSize: "24px",
                textAlign: "center",
                color: "#123051",
              }}
            >
              Would you like to create with...
            </Typography>

            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                marginTop: "40px",
              }}
            >
              <div style={{ height: "142px", width: "144px" }}>
                <img
                  onClick={() => setShowAiGeneration(true)}
                  src={ASSETS.aiGeneratorIcon}
                  style={{ height: "142px", width: "144px" }}
                  alt="img1"
                />
                {/* <Typography
                  variant="p"
                  component="p"
                  style={{
                    fontFamily: "DM Sans",
                    fontWeight: "700",
                    fontSize: "14px",
                    textAlign: "center",
                    color: "#123051",
                    // marginLeft: "40px",
                    // marginRight: "40px",
                  }}
                >
                  Under Construction
                </Typography>
                <Typography
                  variant="p"
                  component="p"
                  style={{
                    fontFamily: "DM Sans",
                    fontWeight: "400",
                    fontSize: "12px",
                    textAlign: "center",
                    color: "#123051",
                  }}
                >
                  Making it Even Better
                </Typography> */}
              </div>
              <Typography
                variant="h5"
                component="h5"
                style={{
                  fontFamily: "DM Sans",
                  fontWeight: "600",
                  fontSize: "24px",
                  textAlign: "center",
                  color: "#123051",
                  marginLeft: "40px",
                  marginRight: "40px",
                }}
              >
                OR
              </Typography>
              <a href="/builder">
                <img
                  // onClick={() => {
                  //   nav("/CreateCustomWheel");
                  //   window.location.reload(true);
                  // }}
                  src={ASSETS.canvasIcon}
                  style={{ height: "142px", width: "144px" }}
                  alt="img1"
                />
              </a>
            </div>
          </div>
        </Box>
      )}
    </Modal>
  );
}

export default NewWheelPopup;
